import {useCallback, useEffect, useRef, useState} from "react";
import {customerServiceSnapshotSchema, type CustomerServiceSnapshot} from "@wenjing/contract/customer-service";
import {api} from "./api";

export type ChatRequest = <T>(path: string, options?: RequestInit) => Promise<T>;

// 两端以保存后的快照为准，避免把人工发送回执误认为第二条消息。
export function useCustomerServiceConversation(conversationId: string, request: ChatRequest = api) {
  const [snapshot, setSnapshot] = useState<CustomerServiceSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sendError, setSendError] = useState("");
  const [draft, setDraft] = useState("");
  const generation = useRef(0), readVersion = useRef(0), busy = useRef(false);
  const identity = useRef({conversationId, request});
  identity.current = {conversationId, request};
  const retry = useRef<{id: string; text: string; key: string} | null>(null);
  const controller = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    if (!conversationId) return;
    const epoch = generation.current, version = ++readVersion.current;
    controller.current?.abort();
    const abort = new AbortController(); controller.current = abort;
    try {
      const result = customerServiceSnapshotSchema.parse(await request<unknown>(`/api/marketing/customer-service/conversations/${encodeURIComponent(conversationId)}`, {signal:abort.signal}));
      if (generation.current === epoch && readVersion.current === version && identity.current.conversationId === conversationId && identity.current.request === request) {
        setSnapshot(result); setError(""); setLoading(false);
      }
    } catch (cause) {
      if (!abort.signal.aborted && generation.current === epoch && readVersion.current === version) {
        setError(cause instanceof Error ? cause.message : "会话同步失败"); setLoading(false);
      }
    }
  }, [conversationId, request]);

  useEffect(() => {
    ++generation.current; setSnapshot(null); setDraft(""); setError(""); setSendError(""); setLoading(Boolean(conversationId)); retry.current = null;
    let disposed = false, timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      if (!disposed && !busy.current && document.visibilityState !== "hidden") await refresh();
      if (!disposed) timer = setTimeout(poll, 2000);
    };
    const visible = () => { if (document.visibilityState !== "hidden" && !busy.current) void refresh(); };
    void refresh(); timer = setTimeout(poll, 2000); document.addEventListener("visibilitychange", visible);
    return () => { disposed = true; ++generation.current; clearTimeout(timer); controller.current?.abort(); document.removeEventListener("visibilitychange", visible); };
  }, [conversationId, refresh]);

  const send = async (text = draft) => {
    text = text.trim();
    if (!text || !conversationId || busy.current || loading || snapshot?.conversation.id !== conversationId || !snapshot.conversation.canReply) return false;
    const epoch = generation.current;
    const isCurrent = () => generation.current === epoch && identity.current.conversationId === conversationId && identity.current.request === request;
    const key = retry.current?.id === conversationId && retry.current.text === text ? retry.current.key : `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    retry.current = {id:conversationId, text, key};
    busy.current = true; setSending(true); setSendError(""); setDraft("");
    ++readVersion.current; controller.current?.abort();
    try {
      await request(`/api/ai/conversations/${encodeURIComponent(conversationId)}/messages`, {method:"POST",body:JSON.stringify({message:text,idempotencyKey:key})});
      if (isCurrent()) { retry.current = null; await refresh(); }
      return true;
    } catch (cause) {
      if (isCurrent()) { setSendError(cause instanceof Error ? cause.message : "发送失败"); setDraft(value => value || text); }
      return false;
    } finally { busy.current = false; setSending(false); }
  };

  const current = snapshot?.conversation.id === conversationId ? snapshot : null;
  return {snapshot:current, messages:current?.messages || [], loading, sending, error:sendError || error, draft, setDraft, send, refresh};
}
